import { getRawQueryParameters } from '@solinkcorp/utils';
import { type LOGOUT_REASON } from './authConstants';

export function isLoginURL(url: string): boolean {
  let pathname: string;
  if (url.startsWith('/')) {
    pathname = url;
  } else {
    // Full URL
    const parsedURL = new URL(url);
    pathname = parsedURL.pathname;
  }

  return pathname.startsWith('/login') || pathname.startsWith('/logout');
}

/**
 * Verify the current customer/tenant is the correct one.
 */
export function isCorrectTenantId(
  location:
    | string
    | {
        search?: string;
        hash?: string;
      },
  currentTenantId: string | undefined,
): boolean {
  const query = getRawQueryParameters(location);

  // Note that for guest access, then tenantId will always be "temporary".
  const requestedCustomerId = query.get('tenantId') || undefined;
  return (
    !currentTenantId ||
    !requestedCustomerId ||
    requestedCustomerId === 'temporary' ||
    currentTenantId === requestedCustomerId
  );
}

export function getImpersonationURL(redirectTo?: string): string {
  let search = '';
  if (redirectTo) {
    const searchParams = new URLSearchParams({ path: redirectTo });
    search = `?${searchParams.toString()}`;
  }
  return `/login/impersonation${search}`;
}

export function getLogoutURL(reason: keyof typeof LOGOUT_REASON, redirectTo?: string): string {
  const params = new URLSearchParams({ reason });
  if (redirectTo) {
    params.set('redirectTo', redirectTo);
  }

  return `/logout?${params.toString()}`;
}

/** Logout the current user. */
export function logout(reason: keyof typeof LOGOUT_REASON, redirectTo?: string): void {
  window.location.replace(`${window.location.origin}${getLogoutURL(reason, redirectTo)}`);
}

/**
 * Converts a location from `window.location` or from the history API to a
 * relative path.
 */
export function locationToRelativePath(location: {
  pathname: string;
  hash?: string;
  search?: string;
}): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function logoutIfAuthFailed(status: Response['status']): void {
  if (status === 401) {
    logout('SESSION_EXPIRED');
  }
}
